import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { UploadCloud } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import type { GameVersion } from '@/models'

/* Matches VersionsController::CONFIG_UPLOAD_STATUSES — every status but archived. */
const UPLOADABLE_STATUSES = new Set(['draft', 'in_review', 'rejected', 'released'])

interface ConfigUploadCardProps {
  version: GameVersion
  canEdit: boolean
}

export function ConfigUploadCard({ version, canEdit }: ConfigUploadCardProps) {
  const { toast } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)

  if (!canEdit || !UPLOADABLE_STATUSES.has(version.status)) return null

  const clear = () => {
    setFiles([])
    if (inputRef.current) inputRef.current.value = ''
  }

  const submit = () => {
    if (files.length === 0) return
    router.post(
      `/versions/${version.id}/config_files`,
      { files },
      {
        forceFormData: true,
        preserveScroll: true,
        onStart: () => setSubmitting(true),
        onFinish: () => setSubmitting(false),
        onSuccess: () => {
          clear()
          toast('Config files uploaded', 'success')
        },
        onError: (errors) => toast(errors.files ?? 'Upload failed', 'danger'),
      },
    )
  }

  return (
    <Card className="space-y-3 p-5">
      <div>
        <h3 className="text-fg text-sm font-semibold">Upload config files</h3>
        <p className="text-fg-subtle mt-0.5 text-xs">
          Pick a ZIP of JSON files, or one or more <span className="tabular-nums">.json</span> files.
          Re-uploading a file replaces its checksum.
        </p>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="border-border bg-bg-subtle text-fg-subtle hover:border-border-strong hover:text-fg-muted focus-visible:outline-accent flex h-24 w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-4 text-center transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-1"
      >
        <UploadCloud className="size-4" />
        <span className="text-sm">
          {files.length > 0 ? `${files.length} file(s) selected` : 'Choose .zip or .json files'}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".json,.zip,application/json,application/zip"
        multiple
        onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
        className="hidden"
      />

      {files.length > 0 && (
        <ul className="text-fg-muted space-y-0.5 text-xs tabular-nums">
          {files.map((file) => (
            <li key={file.name}>{file.name}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end gap-2">
        {files.length > 0 && (
          <Button size="sm" variant="outline" onClick={clear} disabled={submitting}>
            Clear
          </Button>
        )}
        <Button size="sm" onClick={submit} disabled={files.length === 0 || submitting}>
          Upload
        </Button>
      </div>
    </Card>
  )
}
